import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useCopyToClipboard, useSessionStorage, useTimeout } from 'usehooks-ts';
import { v4 } from 'uuid';

import { useI18n } from 'common/hooks';

import './index.css';

type Props = {
  children: React.ReactNode | ((props: { isCopied: boolean }) => React.ReactNode);
  value: string | number | null | undefined;
  className?: string;
  sessionStorageKey?: string;
  timeoutMs?: number;
  'data-test'?: string;
};

export const ClickToCopy: React.FC<Props> = React.memo((props) => {
  const {
    children,
    value,
    className: propsClassName = '',
    sessionStorageKey = 'webtrader:click-to-copy',
    timeoutMs = 3000,
  } = props;
  // Store id to session storage to handle target id changed
  const [storageOrderId, setStorageOrderId] = useSessionStorage(sessionStorageKey, '');

  const [, copy] = useCopyToClipboard();
  const { translate } = useI18n();

  const guid = useMemo(() => v4(), []);
  const formatedID = useMemo(() => String(value) + guid, [guid, value]);

  const isCopied = useMemo(() => storageOrderId === formatedID, [formatedID, storageOrderId]);

  const onReset = useCallback(() => {
    setStorageOrderId('');
  }, [setStorageOrderId]);

  const onCopy = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      copy(String(value));
      setStorageOrderId(formatedID);
    },
    [copy, formatedID, setStorageOrderId, value],
  );

  useTimeout(onReset, storageOrderId ? timeoutMs : null);

  const className = classNames({
    'click-to-copy': true,
    'click-to-copy--active': isCopied,
    [propsClassName]: propsClassName,
    [`${propsClassName}--active`]: propsClassName && isCopied,
  });

  const renderChildren = useCallback(() => {
    if (typeof children === 'function') return children({ isCopied });

    return isCopied ? translate.toText('mobile:order:id:copied') : children;
  }, [children, isCopied, translate]);

  return (
    <div id='click-to-copy' data-test={props['data-test']} className={className} onClick={onCopy}>
      {renderChildren()}
    </div>
  );
});
